import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Auth,
  browserLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  UserCredential,
} from 'firebase/auth';
import {
  addDoc,
  and,
  collection,
  collectionGroup,
  CollectionReference,
  connectFirestoreEmulator,
  doc,
  DocumentData,
  DocumentReference,
  FieldPath,
  Firestore,
  getCountFromServer,
  getDoc,
  getDocs,
  initializeFirestore,
  limit,
  orderBy,
  OrderByDirection,
  Query,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QueryFilterConstraint,
  setDoc,
  SetOptions,
  SnapshotOptions,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import { type Messaging } from 'firebase/messaging';
import {
  connectStorageEmulator,
  deleteObject,
  FirebaseStorage,
  getDownloadURL,
  getStorage,
  ref,
} from 'firebase/storage';
import localforage from 'localforage';
import { formatPhoneNumber } from 'react-phone-number-input';

import { env } from '@/env';
import { importFirebaseMessaging } from '@/hooks/use-firebase-messaging';
import { ActivityLog, ActivityLogAction } from '@/typings/activity-log';
import { User, UserRole } from '@/typings/user';

import { API_LOGIN_PATH, API_LOGOUT_PATH } from './constants';
import rollbar from './rollbar';

export type Path = [path: string, ...pathSegments: string[]];
export type OrderParams = { field: string | FieldPath; order: OrderByDirection };

const firebaseConfig = env.NEXT_PUBLIC_FIREBASE_CONFIG as FirebaseOptions;

export let firebaseApp: FirebaseApp;
export let firestore: Firestore;
export let firebaseAuth: Auth;
export let storage: FirebaseStorage;
export let firebaseMessaging: Messaging;

try {
  if (firebaseConfig) {
    firebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp();

    firebaseAuth = initializeAuth(firebaseApp, {
      persistence: browserLocalPersistence,
    });

    firestore = initializeFirestore(firebaseApp, {
      ignoreUndefinedProperties: true,
    });

    storage = getStorage(firebaseApp);

    if (env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST) {
      connectAuthEmulator(firebaseAuth, `http://${env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST}`);
    }

    if (env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST) {
      const [host, port] = env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST.split(':');
      connectFirestoreEmulator(firestore, host, Number(port));
    }

    if (env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST) {
      const [host, port] = env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST.split(':');
      connectStorageEmulator(storage, host, Number(port));
    }

    if (typeof window !== 'undefined') {
      importFirebaseMessaging()
        .then((messaging) => {
          if (!messaging) return;
          firebaseMessaging = messaging.getMessaging(firebaseApp);
        })
        .catch((err) => rollbar.error('utils/firebase - Failed to import firebase messaging', err));
    }
  }
} catch (error) {
  rollbar.error('Error on firebase setup', error as Error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  // Check if is an object and not an array or null
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function checkForTimestamp(data: unknown): unknown {
  if (data instanceof Timestamp) {
    // Check if is a timestamp and convert to date
    return data.toDate();
  }

  if (Array.isArray(data)) {
    // Check every item in the array
    return checkForTimestampInArray(data);
  }

  if (isRecord(data)) {
    // Check if is an object and check for timestamp prop
    return checkForTimestampProp(data);
  }

  return data;
}

function checkForTimestampInArray<TData>(data: Array<TData>): Array<unknown> {
  return data.map(checkForTimestamp);
}

function checkForTimestampProp(data: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(data).reduce(
    (acc, [key, value]) => {
      acc[key] = checkForTimestamp(value);
      return acc;
    },
    {} as Record<string, unknown>,
  );
}

const converter = {
  toFirestore(data: DocumentData): DocumentData {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): DocumentData {
    const data = snapshot.data(options);
    return { ...checkForTimestampProp(data), id: snapshot.id };
  },
};

// Some firestore hooks need a DOCUMENT arg
export function getDocument<DocumentData>(path: string, ...pathSegments: string[]) {
  return doc(firestore, path, ...pathSegments).withConverter(
    converter,
  ) as DocumentReference<DocumentData>;
}

// Some firestore hooks need a COLLECTION arg
export function getCollection<DocumentData>(path: string, ...pathSegments: string[]) {
  return collection(firestore, path, ...pathSegments).withConverter(
    converter,
  ) as CollectionReference<DocumentData>;
}

// Some firestore hooks need a QUERY arg
export function getQuery<DocumentData>({
  path,
  order,
  query: queryParams = [],
  limit: limitValue,
  allowDeleted = false,
  startAfterDoc,
}: {
  path: Path;
  order: OrderParams[];
  limit?: number;
  query?: [...queryConstraints: QueryConstraint[]];
  allowDeleted?: boolean;
  startAfterDoc?: QueryDocumentSnapshot<DocumentData>;
}) {
  const ref = getCollection(...path);

  // Build query constraints array safely
  const constraints: QueryConstraint[] = [];

  // Add soft delete filter
  if (!allowDeleted) {
    constraints.push(where('deletedAt', '==', null));
  }

  // Add custom query params
  if (queryParams && Array.isArray(queryParams)) {
    constraints.push(...queryParams);
  }

  // Add limit
  if (limitValue && typeof limitValue === 'number' && limitValue > 0) {
    constraints.push(limit(limitValue));
  }

  // Add ordering - validate each order param
  order.forEach((orderParam) => {
    constraints.push(orderBy(orderParam.field, orderParam.order));
  });

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  return query(ref, ...constraints) as Query<DocumentData>;
}

// To use with and | or
export function getCompoundQuery<DocumentData>({
  path,
  order,
  query: queryParams = [],
  limit: limitValue,
}: {
  path: Path;
  order: [OrderParams, ...OrderParams[]];
  limit?: number;
  query?: [...queryConstraints: QueryFilterConstraint[]];
}) {
  const ref = getCollection(...path);

  return query(
    ref,
    and(
      where('deletedAt', '==', null), // TODO: remove this if we're not using soft delete
      ...(queryParams ? queryParams : []),
    ),
    ...(limitValue ? [limit(limitValue)] : []),
    ...order.map(({ field, order }) => orderBy(field, order)),
  ) as Query<DocumentData>;
}

export function getCollectionGroupQuery<DocumentData>({
  collectionName,
  order,
  query: queryParams = [],
  limit: limitValue,
}: {
  collectionName: string;
  order: [OrderParams, ...OrderParams[]];
  limit?: number;
  query?: [...queryConstraints: QueryConstraint[]];
}) {
  const ref = collectionGroup(firestore, collectionName);

  return query(
    ref,
    where('deletedAt', '==', null), // TODO: remove this if we're not using soft delete
    ...(queryParams ? queryParams : []),
    ...(limitValue ? [limit(limitValue)] : []),
    ...order.map(({ field, order }) => orderBy(field, order)),
  ) as Query<DocumentData>;
}

/**
 * Create new document on firestore
 * @param path
 * @param data
 * @param user
 */
export async function createData<T>(path: Path, data: T) {
  const collection = getCollection<T>(...path);
  return addDoc(collection, { ...data, createdAt: Timestamp.now(), deletedAt: null });
}

/**
 * Update document on firestore
 * @param path
 * @param id
 * @param data
 * @param options
 */
export async function updateData<T>(
  path: Path,
  id: string,
  data: Partial<T>,
  options?: SetOptions,
) {
  const item = getDocument<T>(...path, id);
  return setDoc(item, { ...data, updatedAt: Timestamp.now() }, options || { merge: true });
}

/**
 * Delete document on firestore
 * @param path
 * @param id
 * @param data
 * @param options
 */
export async function softDelete(path: Path, id: string) {
  return updateData(path, id, { deletedAt: Timestamp.now() }, { merge: true });
}

/**
 * Get collection data from firestore
 * @param query
 */
export async function getCollectionData<T>(query: Query<T>) {
  const { docs } = await getDocs<T, DocumentData>(query);
  const data = docs.map((d) => ({ ...(d.data() as T), id: d.id }) as T);
  return data;
}

/**
 * Get document data from firestore
 * @param query
 */
export async function getDocumentData<T>(path: Path) {
  const doc = await getDoc<T, DocumentData>(getDocument(...path));

  return { ...(doc.data() as T), id: doc.id } as T;
}

/**
 * Check if the user is already created on firestore
 * If not, create it
 * @param user
 */
export async function checkAndCreateUserOnFirestore(user: UserCredential): Promise<Partial<User>> {
  const { uid, email, displayName, phoneNumber } = user.user;
  const firestoreUser = getDocument<User>('users', uid);
  const documentSnapshot = await getDoc<User, DocumentData>(firestoreUser);

  if (documentSnapshot.exists()) {
    return { ...documentSnapshot.data(), id: documentSnapshot.id };
  }

  const newUser = {
    authId: uid,
    email: email || '',
    name: displayName || '',
    ...(phoneNumber ? { phone: phoneNumber } : {}),
    formattedPhone: formatPhoneNumber(phoneNumber || ''),
    status: 'pending' as const,
    role: UserRole.User,
  };

  await createData(['users'], newUser);
  return newUser;
}

// Storage
export function getStorageRef(url: string) {
  return ref(storage, url);
}

export async function deleteFile(url: string) {
  try {
    const ref = getStorageRef(url);
    return await deleteObject(ref);
  } catch (e) {
    if ((e as Error).message.includes('does not exist')) {
      return;
    }
    throw e;
  }
}

export function getImageUrl(path: string) {
  return getDownloadURL(getStorageRef(path));
}

export async function getCount(path: Path, queryParams: QueryFilterConstraint[]): Promise<number> {
  const ref = getCollection(...path);
  const queryResult = query(ref, and(where('deletedAt', '==', null), ...queryParams));
  const snapshot = await getCountFromServer(queryResult);
  return snapshot.data().count;
}

// Messaging
export async function initMessaging(forceNewToken?: boolean) {
  if (typeof window !== 'undefined') {
    try {
      if ((await localforage.getItem('FCM_TOKEN')) !== null && !forceNewToken) {
        return;
      }
      const registration = await navigator.serviceWorker.ready;

      const messaging = await importFirebaseMessaging();
      if (!messaging) return;
      if (!firebaseMessaging) firebaseMessaging = messaging.getMessaging(firebaseApp);
      const token = await messaging.getToken(firebaseMessaging, {
        vapidKey: env.NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      localforage.setItem('FCM_TOKEN', token);
      return token;
    } catch (error) {
      rollbar.error('utils/firebase - Failed to init firebase messaging', error as Error);
      throw error;
    }
  }
}

export async function getFirebaseMessaging() {
  const messaging = await importFirebaseMessaging();

  if (!messaging) {
    return;
  }

  const supported = await messaging?.isSupported();
  if (!supported || typeof window === 'undefined') return;
  return messaging.getMessaging(firebaseApp);
}

export async function refreshUserToken(idToken: string) {
  await fetch(API_LOGIN_PATH, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
}

export async function clearUserToken() {
  await fetch(API_LOGOUT_PATH, {
    method: 'POST',
  });
}

/**
 * Logs user activity in the Firestore database.
 *
 * @param userId User ID to log the activity for.
 * @param action The action performed by the user, represented as an `ActivityLogAction`.
 * @param context Optional context object containing additional information about the activity.
 */
export async function logUserActivity(
  userId: string,
  action: ActivityLogAction,
  context: Record<string, unknown> = {},
) {
  const collection = getCollection<ActivityLog>('activityLogs');
  const docRef = doc(collection);
  await setDoc(docRef, {
    id: docRef.id,
    userId,
    action,
    context,
    timestamp: Timestamp.now(),
  } satisfies Omit<ActivityLog, 'timestamp'> & { timestamp: Timestamp });
}
